import { useState, useEffect, useCallback, useMemo, ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, RefreshCw, Trash2, ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown,
  Printer, FileSpreadsheet, Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../core/apiClient';
import { ExcelFilterDropdown } from '../../components/ui/excel-filter';

// Types
interface WorkOrder {
  id: string;
  orderNumber: string;
  sequenceNumber: number;
  orderType: string;
  description: string;
  productCode?: string;
  units: number;
  unitType: string;
  prevHours: number;
  realHours: number;
  prevMaterials: number;
  realPurchases: number;
}

interface Brand {
  id: string;
  code: string;
  name: string;
  primaryColor?: string;
}

interface Project {
  id: string;
  projectNumber?: string;
  expedientNumber?: number;
  expedient_number?: number; // snake_case del SQL
  name: string;
  etiquetaExpedient?: string | null;
  etiqueta_expedient?: string | null; // snake_case del SQL
  description?: string;
  brandId?: string;
  brand_id?: string; // snake_case del SQL
  brandName?: string;
  brand_name?: string; // snake_case del SQL
  brandCode?: string;
  brand_code?: string; // snake_case del SQL
  clientId?: string;
  client_id?: string; // snake_case del SQL
  clientName?: string;
  client_name?: string; // snake_case del SQL
  productCode?: string;
  product_code?: string; // snake_case del SQL
  units?: number;
  unitType?: string;
  unit_type?: string; // snake_case del SQL
  duration?: string;
  startDate?: string;
  start_date?: string; // snake_case del SQL
  expectedEndDate?: string;
  expected_end_date?: string; // snake_case del SQL
  prevHours?: number;
  prev_hours?: number; // snake_case del SQL
  realHours?: number;
  real_hours?: number; // snake_case del SQL
  diffHours?: number;
  diff_hours?: number; // snake_case del SQL
  percentCompleted?: number;
  percent_completed?: number; // snake_case del SQL
  estimatedRevenue?: number;
  estimated_revenue?: number; // snake_case del SQL
  actualRevenue?: number;
  actual_revenue?: number; // snake_case del SQL
  estimatedMaterialCost?: number;
  estimated_material_cost?: number; // snake_case del SQL
  actualMaterialCost?: number;
  actual_material_cost?: number; // snake_case del SQL
  marginFactor?: number;
  margin_factor?: number; // snake_case del SQL
  pricePerHour?: number;
  price_per_hour?: number; // snake_case del SQL
  addedValue?: number;
  added_value?: number; // snake_case del SQL
  projectType?: string;
  project_type?: string; // snake_case del SQL
  status?: string;
  notes?: string;
  workOrders?: WorkOrder[];
  budgetId?: string | null;
  createdFromBudget?: number;
}

interface Client {
  id: string;
  name: string;
}


const formatNumber = (num: number | undefined | null, decimals = 2): string => {
  if (num === undefined || num === null || isNaN(Number(num))) return '0';
  return Number(num).toLocaleString('ca-ES', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatDate = (dateStr: string | undefined | null): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ca-ES');
};

// Normalitza un projecte convertint snake_case a camelCase
const normalizeProject = (p: any): Project => ({
  ...p,
  id: p.id,
  projectNumber: p.projectNumber || p.project_number,
  expedientNumber: p.expedientNumber || p.expedient_number,
  name: p.name,
  etiquetaExpedient: p.etiquetaExpedient || p.etiqueta_expedient || null,
  description: p.description,
  brandId: p.brandId || p.brand_id,
  brandName: p.brandName || p.brand_name,
  brandCode: p.brandCode || p.brand_code,
  clientId: p.clientId || p.client_id,
  clientName: p.clientName || p.client_name,
  productCode: p.productCode || p.product_code,
  units: p.units || 1,
  unitType: p.unitType || p.unit_type || 'Ut',
  duration: p.duration,
  startDate: p.startDate || p.start_date,
  expectedEndDate: p.expectedEndDate || p.expected_end_date,
  prevHours: Number(p.prevHours || p.prev_hours || 0),
  realHours: Number(p.realHours || p.real_hours || 0),
  diffHours: Number(p.diffHours || p.diff_hours || 0),
  percentCompleted: Number(p.percentCompleted || p.percent_completed || 0),
  estimatedRevenue: Number(p.estimatedRevenue || p.estimated_revenue || 0),
  actualRevenue: Number(p.actualRevenue || p.actual_revenue || 0),
  estimatedMaterialCost: Number(p.estimatedMaterialCost || p.estimated_material_cost || 0),
  actualMaterialCost: Number(p.actualMaterialCost || p.actual_material_cost || 0),
  marginFactor: Number(p.marginFactor || p.margin_factor || 0),
  pricePerHour: Number(p.pricePerHour || p.price_per_hour || 0),
  addedValue: Number(p.addedValue || p.added_value || 0),
  projectType: p.projectType || p.project_type,
  status: p.status,
  notes: p.notes,
  budgetId: p.budgetId || p.budget_id || null,
  createdFromBudget: p.createdFromBudget || p.created_from_budget || 0,
});

// Columnes base + de Previsions (única vista, 2026-10-08 — abans hi havia Execució/Ratis
// commutables, ara nomes es mostra Previsions) — mateix contingut que les capçaleres reals.
const BASE_HEADERS = ['Marca', 'Num', 'Data', 'Data Lliurament', 'Descripció', 'Client', 'Tipus'];
const PREVISIONS_HEADERS = ['Prev. Hores', 'Real Hores', 'Dif. Hores', '% Real.', 'Prev. Ingr.', 'Real Ingr.', 'Dif. Ingr.'];

function expedientRowCells(project: Project): string[] {
  return [
    project.brandCode || '',
    String(project.expedientNumber ?? ''),
    formatDate(project.startDate),
    formatDate(project.expectedEndDate),
    project.etiquetaExpedient || project.name || '',
    project.clientName || '',
    project.productCode || '',
    formatNumber(project.prevHours ?? 0),
    formatNumber(project.realHours ?? 0),
    formatNumber(project.diffHours ?? 0),
    formatNumber(project.percentCompleted ?? 0),
    formatNumber(project.estimatedRevenue ?? 0),
    formatNumber(project.actualRevenue ?? 0),
    formatNumber((project.actualRevenue ?? 0) - (project.estimatedRevenue ?? 0)),
  ];
}

function exportExpedientsToExcel(filename: string, projects: Project[]): void {
  const headers = [...BASE_HEADERS, ...PREVISIONS_HEADERS];
  const rows = projects.map((p) => expedientRowCells(p));
  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
    <head><meta charset="utf-8">
    <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
    <x:Name>Expedients</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
    </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
    </head>
    <body>
    <table border="1">
      <tr><th colspan="${headers.length}" style="font-weight:bold; font-size:14px;">Expedients de Taller</th></tr>
      <tr></tr>
      <tr>${headers.map((h) => `<th>${h}</th>`).join('')}</tr>
      ${rows.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join('')}</tr>`).join('')}
    </table>
    </body></html>`;

  const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function printExpedients(projects: Project[]): void {
  const headers = [...BASE_HEADERS, ...PREVISIONS_HEADERS];
  const rows = projects.map((p) => expedientRowCells(p));
  const html = `<!DOCTYPE html>
<html lang="ca">
<head>
  <meta charset="utf-8">
  <title>Expedients de Taller</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 8.5pt; color: #1e293b; padding: 1.2cm; }
    h1 { font-size: 13pt; margin-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #1e293b; color: white; padding: 4px 6px; text-align: left; font-size: 7.5pt; }
    th.r, td.r { text-align: right; }
    td { padding: 3px 6px; border-bottom: 1px solid #e2e8f0; }
    tr:nth-child(even) td { background: #f8fafc; }
    @page { size: A4 landscape; margin: 1cm; }
  </style>
</head>
<body>
  <h1>Expedients de Taller — Imprès el ${new Date().toLocaleDateString('ca-ES')}</h1>
  <table>
    <thead><tr>${headers.map((h, i) => `<th class="${i >= BASE_HEADERS.length ? 'r' : ''}">${h}</th>`).join('')}</tr></thead>
    <tbody>
      ${rows.map((r) => `<tr>${r.map((c, i) => `<td class="${i >= BASE_HEADERS.length ? 'r' : ''}">${c}</td>`).join('')}</tr>`).join('')}
    </tbody>
  </table>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); win.close(); }, 600);
}

export default function Expedients() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [compositeCodes, setCompositeCodes] = useState<{ id: number; code: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  type SortField = 'startDate' | 'expectedEndDate' | 'name' | 'clientName' | 'productCode';
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronsUpDown className="h-3 w-3 ml-0.5 text-gray-400" />;
    return sortDir === 'asc'
      ? <ChevronUp className="h-3 w-3 ml-0.5 text-blue-600" />
      : <ChevronDown className="h-3 w-3 ml-0.5 text-blue-600" />;
  };

  const sortedProjects = useMemo(() => {
    if (!sortField) return projects;
    const av = (p: Project) => (p[sortField] ?? '') as string;
    return [...projects].sort((a, b) => {
      const x = av(a);
      const y = av(b);
      if (x < y) return sortDir === 'asc' ? -1 : 1;
      if (x > y) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [projects, sortField, sortDir]);

  // Node del slot d'accions a la capçalera de Taller.tsx (Imprimir/Excel es
  // renderitzen alla via portal, a l'alçada del titol "Taller").
  const [headerActionsEl, setHeaderActionsEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setHeaderActionsEl(document.getElementById('taller-header-actions'));
  }, []);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedType !== 'all') params.append('productCode', selectedType);
      if (selectedBrand !== 'all') params.append('brandId', selectedBrand);
      if (showOnlyActive) params.append('status', 'active');
      
      const response = await api.get<{ success: boolean; data: any[] }>(
        `/taller/projects?${params.toString()}`
      );
      if (response.success && response.data) {
        const projectsData = Array.isArray(response.data) ? response.data : [];
        const normalized = projectsData.map(normalizeProject);
        setProjects(normalized);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedType, selectedBrand, showOnlyActive]);

  const fetchClients = async () => {
    try {
      const response = await api.get<{ success: boolean; data: Client[] }>('/clients?limit=9999&sortBy=name');
      if (response.success) {
        setClients(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get<{ success: boolean; data: Brand[] }>('/brands');
      if (response.success) {
        setBrands(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchCompositeCodes = async () => {
    try {
      const response = await api.get<{ success: boolean; data: { id: number; code: string; name: string }[] }>(
        '/catalog/composite-codes'
      );
      if (response.success && response.data) {
        setCompositeCodes(response.data);
      }
    } catch (error) {
      console.error('Error fetching composite codes:', error);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchClients();
    fetchBrands();
    fetchCompositeCodes();
  }, [fetchProjects]);

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleDelete = async (project: Project) => {
    if (!confirm(`Segur que vols eliminar l'expedient ${project.expedientNumber} - ${project.name}?`)) {
      return;
    }

    try {
      await api.delete(`/taller/projects/${project.id}`);
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Error en eliminar');
    }
  };

  return (
    <div className="space-y-4 p-6">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-medium text-foreground whitespace-nowrap">Marca:</span>
          <ExcelFilterDropdown
            label="Marca"
            options={brands.map((brand) => ({ value: brand.id, label: brand.name }))}
            selected={selectedBrand === 'all' ? [] : selectedBrand.split(',')}
            onApply={(values) => setSelectedBrand(values.length === 0 ? 'all' : values.join(','))}
          />
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs font-medium text-foreground whitespace-nowrap">Tipus de producte:</span>
          <ExcelFilterDropdown
            label="Tipus"
            options={compositeCodes.map((cc) => ({ value: cc.code, label: `${cc.code} - ${cc.name}` }))}
            selected={selectedType === 'all' ? [] : selectedType.split(',')}
            onApply={(values) => setSelectedType(values.length === 0 ? 'all' : values.join(','))}
          />
        </div>

        <div className="relative w-48 shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Cercar per num, nom o client..."
            value={searchTerm}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            className="pl-9 text-xs"
          />
        </div>
        <label className="flex items-center gap-2 text-xs whitespace-nowrap shrink-0">
          <input
            type="checkbox"
            checked={showOnlyActive}
            onChange={(e) => setShowOnlyActive(e.target.checked)}
            className="rounded"
          />
          Nomes actius
        </label>
        <Button variant="outline" size="icon" onClick={fetchProjects} className="shrink-0">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>

        <button
          onClick={() => navigate('/taller/expedients/new')}
          className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-xs hover:bg-gray-700 transition-colors shrink-0 whitespace-nowrap"
        >
          <Plus className="h-3.5 w-3.5" /> Nou Expedient
        </button>
      </div>

      {/* Imprimir/Excel — renderitzats via portal a la capçalera de Taller.tsx,
          a l'alçada del titol "Taller" i alineats a la dreta. */}
      {headerActionsEl && createPortal(
        <>
          <button
            onClick={() => printExpedients(sortedProjects)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-xs"
          >
            <Printer className="h-3.5 w-3.5" /> Imprimir
          </button>
          <button
            onClick={() => exportExpedientsToExcel('Expedients_Previsions.xls', sortedProjects)}
            className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-xs"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
          </button>
        </>,
        headerActionsEl
      )}

      {/* Table */}
      <div className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px] text-muted-foreground">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="w-8 px-2 py-2"></th>
                <th className="px-2 py-2 text-center font-medium text-muted-foreground">Marca</th>
                <th className="px-2 py-2 text-left font-medium text-muted-foreground">Num</th>
                {([
                  { field: 'startDate' as const,      label: 'Data',            cls: 'text-left' },
                  { field: 'expectedEndDate' as const, label: 'Data Lliurament', cls: 'text-left' },
                  { field: 'name' as const,            label: 'Descripcio',     cls: 'text-left min-w-[200px]' },
                  { field: 'clientName' as const,      label: 'Client',          cls: 'text-left' },
                  { field: 'productCode' as const,     label: 'Tipus',           cls: 'text-center' },
                ]).map(({ field, label, cls }) => (
                  <th key={field} className={`px-2 py-2 font-medium text-muted-foreground ${cls}`}>
                    <button
                      onClick={() => handleSort(field)}
                      className={`inline-flex items-center text-muted-foreground hover:text-foreground transition-colors ${sortField === field ? 'font-semibold' : ''}`}
                    >
                      {label}<SortIcon field={field} />
                    </button>
                  </th>
                ))}
                <th className="px-2 py-2 text-right font-medium text-muted-foreground">Prev. Hores</th>
                <th className="px-2 py-2 text-right font-medium text-muted-foreground">Real Hores</th>
                <th className="px-2 py-2 text-right font-medium text-muted-foreground">Dif. Hores</th>
                <th className="px-2 py-2 text-right font-medium text-muted-foreground">% Real.</th>
                <th className="px-2 py-2 text-right font-medium text-muted-foreground">Prev. Ingr.</th>
                <th className="px-2 py-2 text-right font-medium text-muted-foreground">Real Ingr.</th>
                <th className="px-2 py-2 text-right font-medium text-muted-foreground">Dif. Ingr.</th>
                <th className="px-2 py-2 text-center font-medium text-muted-foreground">Accions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={20} className="px-4 py-8 text-center text-gray-500">
                    Carregant...
                  </td>
                </tr>
              ) : projects.length === 0 ? (
                <tr>
                  <td colSpan={20} className="px-4 py-8 text-center text-gray-500">
                    No s&apos;han trobat expedients
                  </td>
                </tr>
              ) : (
                sortedProjects.map((project) => (
                  <>
                    {/* Project Row */}
                    <tr
                      key={project.id}
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/taller/expedients/${project.id}`)}
                    >
                      <td className="px-2 py-2">
                        <button className="p-1" onClick={(e) => { e.stopPropagation(); toggleRow(project.id); }}>
                          {expandedRows.has(project.id) ? (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-2 py-2 text-center">
                        {project.brandCode && (
                          <span 
                            className="inline-block px-2 py-0.5 rounded text-xs font-medium text-white"
                            style={{ backgroundColor: brands.find(b => b.id === project.brandId)?.primaryColor || '#6b7280' }}
                          >
                            {project.brandCode}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 font-mono font-medium text-muted-foreground">
                        {project.expedientNumber}
                      </td>
                      <td className="px-2 py-2 text-muted-foreground">
                        {formatDate(project.startDate)}
                      </td>
                      <td className="px-2 py-2 text-muted-foreground">
                        {formatDate(project.expectedEndDate)}
                      </td>
                      <td className="px-2 py-2 font-normal">
                        {project.etiquetaExpedient || project.name}
                      </td>
                      <td className="px-2 py-2 text-muted-foreground">{project.clientName}</td>
                      <td className="px-2 py-2 text-center">{project.productCode}</td>
                      <td className="px-2 py-2 text-right">{formatNumber(project.prevHours ?? 0)}</td>
                      <td className="px-2 py-2 text-right">{formatNumber(project.realHours ?? 0)}</td>
                      <td className={`px-2 py-2 text-right ${(project.diffHours ?? 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatNumber(project.diffHours ?? 0)}
                      </td>
                      <td className="px-2 py-2 text-right">{formatNumber(project.percentCompleted ?? 0)}</td>
                      <td className="px-2 py-2 text-right">{formatNumber(project.estimatedRevenue ?? 0)}</td>
                      <td className="px-2 py-2 text-right">{formatNumber(project.actualRevenue ?? 0)}</td>
                      <td className={`px-2 py-2 text-right ${((project.actualRevenue ?? 0) - (project.estimatedRevenue ?? 0)) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatNumber((project.actualRevenue ?? 0) - (project.estimatedRevenue ?? 0))}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => navigate(`/taller/expedients/${project.id}`)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                            title="Veure detall"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(project)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded OTs */}
                    {expandedRows.has(project.id) && project.workOrders && project.workOrders.length > 0 && (
                      <tr key={`${project.id}-ots`}>
                        <td colSpan={20} className="px-0 py-0 bg-gray-50">
                          <div className="px-8 py-3">
                            <table className="w-full text-[12px] text-muted-foreground">
                              <thead>
                                <tr className="text-muted-foreground">
                                  <th className="px-2 py-1 text-left font-medium">OT</th>
                                  <th className="px-2 py-1 text-left font-medium">Tipus</th>
                                  <th className="px-2 py-1 text-left font-medium">Descripcio</th>
                                  <th className="px-2 py-1 text-center font-medium">Cod.</th>
                                  <th className="px-2 py-1 text-right font-medium">Ut</th>
                                  <th className="px-2 py-1 text-right font-medium">Prev. Hores</th>
                                  <th className="px-2 py-1 text-right font-medium">Real Hores</th>
                                  <th className="px-2 py-1 text-right font-medium">Prev. Mat.</th>
                                  <th className="px-2 py-1 text-right font-medium">Real Compres</th>
                                </tr>
                              </thead>
                              <tbody>
                                {project.workOrders.map((ot) => (
                                  <tr key={ot.id} className="border-t border-gray-200">
                                    <td className="px-2 py-1 font-mono">
                                      {project.expedientNumber}.{String(ot.sequenceNumber).padStart(2, '0')}
                                    </td>
                                    <td className="px-2 py-1">{ot.orderType}</td>
                                    <td className="px-2 py-1">{ot.description}</td>
                                    <td className="px-2 py-1 text-center">{ot.productCode}</td>
                                    <td className="px-2 py-1 text-right">{formatNumber(ot.units)} {ot.unitType}</td>
                                    <td className="px-2 py-1 text-right">{formatNumber(ot.prevHours)}</td>
                                    <td className="px-2 py-1 text-right">{formatNumber(ot.realHours)}</td>
                                    <td className="px-2 py-1 text-right">{formatNumber(ot.prevMaterials)}</td>
                                    <td className="px-2 py-1 text-right">{formatNumber(ot.realPurchases)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))
              )}
            </tbody>
            {/* Totals footer */}
            {projects.length > 0 && (
              <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                <tr className="font-semibold">
                  <td colSpan={9} className="px-2 py-2 text-right">Totals:</td>
                  <td className="px-2 py-2 text-right">{formatNumber(projects.reduce((s, p) => s + Number(p.prevHours || 0), 0))}</td>
                  <td className="px-2 py-2 text-right">{formatNumber(projects.reduce((s, p) => s + Number(p.realHours || 0), 0))}</td>
                  <td className="px-2 py-2 text-right">{formatNumber(projects.reduce((s, p) => s + Number(p.diffHours || 0), 0))}</td>
                  <td className="px-2 py-2 text-right"></td>
                  <td className="px-2 py-2 text-right">{formatNumber(projects.reduce((s, p) => s + Number(p.estimatedRevenue || 0), 0))}</td>
                  <td className="px-2 py-2 text-right">{formatNumber(projects.reduce((s, p) => s + Number(p.actualRevenue || 0), 0))}</td>
                  <td className="px-2 py-2 text-right">{formatNumber(projects.reduce((s, p) => s + (Number(p.actualRevenue || 0) - Number(p.estimatedRevenue || 0)), 0))}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
